import axiosInstance from '@/libs/axios'
import { storePagination } from '@/mixins/store'
import { AxiosError } from 'axios'
import { isUserLoggedIn } from '@/auth/utils'

export const getInitialAtmCatalogFilters = () => ({
  name: '',
  groupId: null,
})

const getInitialState = () => ({
  ...storePagination.state(),
  filters: getInitialAtmCatalogFilters(),
  catalog: [],
  groups: [],

  productCache: {},

  catalogError: '',
  catalogLoading: false,
})

export default {
  namespaced: true,
  state: getInitialState(),
  modules: {},

  getters: {
    getGroups(state) {
      return state.groups || []
    },

    getProductCache(state) {
      return state.productCache || {}
    },

    isLoadingCatalog(state) {
      return state.catalogLoading
    },

    isLoadedAllCatalog(state) {
      return state.paging.allRowsLoaded
    },
  },

  mutations: {
    ...storePagination.mutations,

    SET_PRODUCT_CACHE(state, { key, value }) {
      state.productCache = {
        ...state.productCache,
        [key]: value,
      }
    },

    SET_CATALOG(state, catalog) {
      state.catalog = catalog || []
    },
    SET_GROUPS(state, groups) {
      state.groups = groups || []
    },

    SET_CATALOG_LOADING(state, loading) {
      state.catalogLoading = loading
    },

    SET_CATALOG_ERROR(state, { error }) {
      state.catalogError = error
    },

    SET_FILTERS(state, filters) {
      state.filters = filters || getInitialAtmCatalogFilters()
    },

    CLEAN_CATALOG_ERROR(state) {
      state.catalogError = ''
    },
    CLEAN_INFINITE_SCROLL(state) {
      const { catalog, paging, productCache } = getInitialState()
      state.catalog = catalog
      state.productCache = productCache
      state.paging.allRowsLoaded = paging.allRowsLoaded
      state.paging.currentPage = paging.currentPage
    },

    CLEAN_STATE(state) {
      const defaultData = getInitialState()
      Object.keys(defaultData).forEach(key => {
        state[key] = defaultData[key]
      })
    },
  },

  actions: {
    async loadAtmCatalog({ commit, dispatch }) {
      // Seta loading imediatamente para ocultar o header no layout
      commit('SET_CATALOG_LOADING', true)

      // Limpa o catálogo atual antes de recarregar
      commit('CLEAN_INFINITE_SCROLL')
      commit('SET_CATALOG', [])

      try {
        await dispatch('fetchCatalogGroup')
        await dispatch('fetchAtmProductCatalog', { skipLoadingCheck: true })
      } finally {
        commit('SET_CATALOG_LOADING', false)
      }
    },

    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },
    async setFiltersAndSearch({ state, commit, dispatch }, filters) {
      commit('SET_FILTERS', { ...state.filters, ...filters })
      commit('CLEAN_INFINITE_SCROLL')

      await dispatch('fetchAtmProductCatalog')
    },

    cleanCatalog({ commit }) {
      commit('CLEAN_INFINITE_SCROLL')
      commit('SET_FILTERS')
      commit('SET_CATALOG')
    },

    async fetchAtmProductCatalog(
      { state, commit, rootState, rootGetters, dispatch },
      { skipLoadingCheck = false } = {}
    ) {
      // Permite pular a verificação de loading quando chamado de loadAtmCatalog
      if (!skipLoadingCheck && (state.catalogLoading || state.paging.allRowsLoaded)) return

      commit('SET_CATALOG_LOADING', true)
      commit('CLEAN_CATALOG_ERROR')

      const customer = rootState.pages.pdv.payBox.clientForm
      const payBoxConfig = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const storeId = payBoxConfig?.store?.id
      const eventId = payBoxConfig?.eventId

      const params = {
        // brandId: state.filters.brand,
        // subgroupId: state.filters.subgroup,
        // departmentId: state.filters.department,
        name: state.filters.name,
        groupId: state.filters.groupId,
        eventId,
        customerId: customer.id,
        pageSize: 16,
        pageIndex: state.paging.currentPage - 1,
      }

      try {
        const { data } = await axiosInstance.get(`/api/sales/pay-box-sku/store/${storeId}/atm`, {
          params,
        })

        if (data.results.length === 0) {
          commit('SET_ALL_ROWS_LOADED', true)
        } else {
          const catalogItems = state.catalog
          catalogItems.push(...data?.results)

          commit('SET_CATALOG', catalogItems)
          commit('SET_CURRENT_PAGE', state.paging.currentPage + 1)
        }

        dispatch('reloadUntilScrollShow')
      } catch (error) {
        console.error('[atm catalog] load items', error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 401 && !isUserLoggedIn()) {
            commit('SET_CATALOG_ERROR', {
              error: 'Acesso expirado, por favor contate um atendente.',
            })
          }
        }
      } finally {
        commit('SET_CATALOG_LOADING', false)
      }
    },

    async fetchCatalogGroup({ commit, rootGetters }) {
      const payBoxConfig = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const storeId = payBoxConfig?.store?.id
      const eventId = payBoxConfig?.eventId

      const { data } = await axiosInstance.get(
        `/api/sales/pay-box-catalog-group/store/${storeId}/atm`,
        {
          params: { eventId },
        }
      )

      commit('SET_GROUPS', data)
    },

    async fetchProductToCart({ commit, dispatch, getters, rootGetters }, { ean, skuId, isKit }) {
      const payBoxConfig = rootGetters['pages/pdv/payBoxConfiguration/currentPayboxConfiguration']
      const eventId = payBoxConfig?.eventId
      const salePriceTable = rootGetters['pages/pdv/payBox/getPriceTable']
      const priceContextKey = eventId != null
        ? `event_${eventId}`
        : `priceTable_${salePriceTable?.id ?? 'default'}`
      const key = `${skuId}-isKit_${isKit}-${priceContextKey}`
      const productFound = getters.getProductCache[key]
      if (eventId == null && productFound) {
        return productFound
      }

      const productFetched = await dispatch(
        'pages/pdv/saleProducts/fetchProductByEan',
        {
          ean,
          eventId,
          priceTableId: eventId == null ? salePriceTable?.id : null,
          isAtm: true,
        },
        { root: true }
      )

      commit('SET_PRODUCT_CACHE', { key, value: productFetched })

      return productFetched
    },

    async reloadUntilScrollShow({ dispatch }) {
      const catalogContainer = document.querySelector('#atm-catalog-main-container')

      const hasVerticalScroll = catalogContainer?.scrollHeight > catalogContainer?.clientHeight

      if (catalogContainer && !hasVerticalScroll) {
        await dispatch('fetchAtmProductCatalog')
      }
    },
  },
}
